import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { catchError, map, of, take } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { DropdownPosition } from 'src/app/components/shared/form/components/form-select/form-select.constants';
import {
  STATUS_OPTIONS,
  VALIDATION_MESSAGE,
} from 'src/app/components/shared/shared-constants';
import { SpecialCharacters } from 'src/app/core/enums';
import { LocationProfile } from 'src/app/core/models/admin';
import { LocationService } from 'src/app/core/services/admin/location/location.service';
import { ErrorhandleService } from 'src/app/core/services/error-handling/error-handle/errorhandle.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import {
  HOUR_RANGES,
  LOCATION_FORM_LABEL,
  LOCATION_SETUP_OPTION,
  MINUTE_RANGES,
  TABS,
} from '../location-profile.constants';
import {
  CONSTS,
  LocationFormCopy,
  LocationPropertiesTypes,
  Option,
} from './location-form.constants';

@Component({
  selector: 'app-location-form',
  templateUrl: './location-form.component.html',
  styleUrls: ['./location-form.component.scss'],
  standalone: false,
})
export class LocationFormComponent implements OnInit {
  @Input() locationItem!: LocationProfile;
  @Input() edit!: boolean;
  @Output() changeTab = new EventEmitter();
  @Output() formStatus = new EventEmitter();

  readonly destroy = inject(DestroyRef);
  locationForm!: FormGroup;
  locationSetupOption = LOCATION_SETUP_OPTION.slice().sort((a, b) =>
    a.label.localeCompare(b.label),
  ) as Option[];
  hourRange = HOUR_RANGES;
  minuteRange = MINUTE_RANGES;
  formDirty = false;
  formValidAndDirty = false;
  copyOfInitialState: LocationFormCopy = {} as LocationFormCopy;
  formState = {
    canSubmit: false,
    canReset: false,
  };
  readonly CONSTANTS = {
    ...LOCATION_FORM_LABEL,
    ...VALIDATION_MESSAGE,
    ...CONSTS,
  };

  readonly STATUS_OPTIONS = STATUS_OPTIONS;
  readonly REGEX = SpecialCharacters;
  readonly DropdownPosition = DropdownPosition;
  originalLocationCode!: string;
  timezoneOptionList!: Option[];

  constructor(
    private readonly fb: FormBuilder,
    public readonly commonUtils: CommonUtils,
    private readonly locationService: LocationService,
    private readonly errorHandler: ErrorhandleService,
    private readonly matDialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.createReturnState();
    this.subscribes();
  }

  hasApiKey(): boolean {
    return this.locationItem?.apiKey?.length > 0;
  }

  createReturnState(): void {
    this.copyOfInitialState = {
      ...this.locationForm.getRawValue(),
    };
  }

  resetLocationForm(): void {
    this.locationForm.reset(this.copyOfInitialState);
    this.locationItem = this.getFormData();
    this.locationService
      .updateLocation(this.locationItem, this.locationItem?.locationCode)
      .pipe(
        take(1),
        map((res) => {
          if (res?.code === 200 && res?.response) {
            this.resetFormState(res.response);
          }
        }),
        catchError((res) => {
          this.errorHandler.handleError<LocationFormComponent>(
            res,
            res.code,
            'Location Form: Failed to reset profile in location form component.',
          );
          return of(false);
        }),
      )
      .subscribe();
  }

  getAllTimezones(): void {
    // back-end will always send as "locale utc/gmt-offset"
    // split string for model as back-end only accepts locale part

    // offset will be displayed as label to user
    this.locationService
      .getAllTimezones()
      .pipe(
        take(1),
        map((data) => {
          this.timezoneOptionList = data?.response.map((timezones: string) => {
            return {
              label: timezones,
              value:
                timezones?.split(' ').length > 1
                  ? timezones.split(' ')[0]
                  : timezones,
            };
          });
        }),
      )
      .subscribe();
  }

  initForm(): void {
    this.originalLocationCode = this.locationItem?.locationCode;
    this.getAllTimezones();
    const locationProps: LocationPropertiesTypes = {
      locationCode: this.locationItem?.locationCode ?? '',
      locationName: this.locationItem?.locationName ?? '',
      pkgSlic: this.locationItem?.pkgSlic ?? '',
      hubSlic: this.locationItem?.hubSlic ?? '',
      mnemonic: this.locationItem?.mnemonic ?? '',
      region: this.locationItem?.region ?? '',
      district: this.locationItem?.district ?? '',
      ospId: this.locationItem?.ospId ?? '',
      locationSetup: this.locationItem?.locationSetup ?? '',
      gmtTimeZone: this.locationItem?.gmtTimeZone ?? '',
      apiKey: this.locationItem?.apiKey ?? '',
    };

    this.locationForm = this.fb.group({
      locationCode: [locationProps.locationCode, Validators.required],
      locationName: [locationProps.locationName, Validators.required],
      pkgSlic: [locationProps.pkgSlic, Validators.required],
      hubSlic: [locationProps.hubSlic],
      mnemonic: [locationProps.mnemonic],
      region: [locationProps.region, Validators.required],
      district: [locationProps.district, Validators.required],
      ospId: [locationProps.ospId],
      locationSetup: [locationProps.locationSetup],
      gmtTimeZone: [locationProps.gmtTimeZone, Validators.required],
      // placing this control here, even though its unused, the value will be pulled from
      // locationForm.getRawValue preserving its state through the edit process
      apiKey: [locationProps.apiKey],
    });

    // user could click tab and leave early so prep initial timezone value
    const tmzValue = this.locationItem.gmtTimeZone?.split(' ') ?? [];

    if (tmzValue.length > 1) {
      this.locationItem.gmtTimeZone = tmzValue[0];
    }
  }

  get form() {
    return this.locationForm.controls;
  }

  // subscribing to form value changes to determine dirty state of form and enable/disable action buttons accordingly
  subscribes(): void {
    this.locationForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((change) => {
        const dirty = this.locationForm.dirty;
        const valid = this.locationForm.valid;

        this.formDirty = dirty;
        this.formValidAndDirty = dirty && valid;

        this.formState = {
          canReset: this.formDirty,
          canSubmit: this.formValidAndDirty,
        };
        this.formStatus.emit(this.formDirty);
      });
  }

  getFormData(): LocationProfile {
    this.locationItem = {
      address: { ...this.locationItem?.address },
      locationGroups: this.locationItem?.locationGroups ?? [],
      ...this.locationItem,
      ...this.locationForm.getRawValue(),
    };

    this.locationItem.gmtTimeZone =
      this.locationItem.gmtTimeZone?.split(/ +/)[0] ?? '';

    // ensure these fields are all caps for now
    // until back-end creates verbose error handling indicating which
    // property is the cause of an error (dev env only)
    this.locationItem.locationCode =
      this.locationItem.locationCode?.toLocaleUpperCase();
    this.locationItem.pkgSlic = this.locationItem.pkgSlic?.toLocaleUpperCase();
    this.locationItem.hubSlic = this.locationItem.hubSlic?.toLocaleUpperCase();
    this.locationItem.mnemonic =
      this.locationItem.mnemonic?.toLocaleUpperCase();

    return this.locationItem;
  }

  resetFormState(profile: LocationProfile): void {
    // update locationItem & state
    if (profile) {
      this.locationItem = profile;
      this.copyOfInitialState = this.locationItem;
      this.locationService.setProfileState$(this.locationItem);
      this.formDirty = false;
      this.formStatus.emit(this.formDirty);
    }
  }

  submitLocationForm(): void {
    this.locationItem = this.getFormData();
    if (!this.edit) {
      this.changeTab.emit({
        data: { profile: this.locationItem, edit: this.edit },
        tab: TABS.ADDRESS,
      });
    } else {
      this.locationService
        .updateLocation(this.locationItem, this.originalLocationCode)
        .pipe(
          take(1),
          map((res) => {
            this.matDialog.closeAll();
            if (res?.code === 200 && res?.response) {
              this.resetFormState(res?.response);
              this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
            }
          }),
          catchError((res) => {
            this.errorHandler.handleError<LocationFormComponent>(
              res,
              res.code,
              'Location Profile: Failed to update profile in location form component.',
            );
            return of(false);
          }),
        )
        .subscribe();
    }
  }
}
